import React, { useState, useEffect } from 'react';
// Added RefreshCw to the list of icons imported from lucide-react
import { Mail, ChevronRight, Lock, User as UserIcon, AlertCircle, Globe, X, Ticket, ChevronDown, Check, RefreshCw } from 'lucide-react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { 
  auth, 
  db, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider 
} from './firebaseConfig';
import { SupportedLanguage, User, ReferralStats } from './types';
import { LOGO_URL, BRAND_COLORS } from './constants';
import { LanguageSelector } from './components/LanguageSelector';
import { useTranslation } from 'react-i18next';

// Updated props type to use SupportedLanguage instead of string to resolve type mismatch in App.tsx
export const AuthScreen = ({ 
    lang, 
    setLang, 
    onLoginSuccess 
}: { 
    lang: SupportedLanguage, 
    setLang: (l: SupportedLanguage) => void, 
    onLoginSuccess: (user: User) => void
}) => {
    const { t } = useTranslation();
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [username, setUsername] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [referralCodeInput, setReferralCodeInput] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const generateUniqueCode = (name: string) => {
        const cleanName = name.replace(/\s+/g, '').substring(0, 4).toUpperCase();
        const rand = Math.floor(1000 + Math.random() * 9000);
        return `VITACORE-${cleanName}${rand}`;
    };

    // Use the centralized checkUsernameAvailability from profileService
    // This avoids duplication and ensures proper error handling
    const checkUsernameAvailability = async (username: string): Promise<boolean> => {
        try {
            const { checkUsernameAvailability: checkAvailability } = await import('./services/profileService');
            return await checkAvailability(username);
        } catch (error: unknown) {
            const err = error as { code?: string; message?: string };
            console.error('[AUTH] ⚠️ Error checking username availability (non-blocking):', {
                code: err.code,
                message: err.message,
                username
            });
            // Fail-safe: if check fails, allow username (don't block signup)
            return true;
        }
    };

    const checkOrCreateUserDoc = async (firebaseUser: { uid: string; email: string | null; displayName: string | null; photoURL: string | null }, userName?: string, userUsername?: string, provider: 'password' | 'google' = 'password') => {
        // CRITICAL: Ensure auth is complete - firebaseUser.uid must be valid
        if (!firebaseUser || !firebaseUser.uid) {
            console.error('[AUTH] ❌ Invalid Firebase user: missing UID');
            throw new Error('Invalid Firebase user: missing UID');
        }
        
        const currentEmail = firebaseUser.email || email;
        if (!currentEmail) {
            console.error('[AUTH] ❌ Missing email for user:', firebaseUser.uid);
            throw new Error('Email is required for user profile');
        }

        // ADMIN SETUP: Check if admin email
            const ADMIN_EMAILS = ['cristianp.business8@gmail.com', 'pellegrinoluca123@gmail.com'];
        const isAdmin = ADMIN_EMAILS.some(adminEmail => currentEmail.toLowerCase() === adminEmail.toLowerCase());
            
        // Extract display name
        const displayName = userName || firebaseUser.displayName || currentEmail.split('@')[0] || t('errors.new_user');
        
        // CRITICAL: Use the centralized ensureUserProfile function
        // This handles all Firestore writes safely with proper sanitization
        try {
            console.log('[AUTH] 🔐 Creating/updating profile for authenticated user:', {
                uid: firebaseUser.uid,
                email: currentEmail,
                provider
            });

            // Handle referral code (fail-safe - don't block profile creation)
            let referrerId: string | undefined = undefined;
            let referralCode: string | undefined = undefined;
            
            if (referralCodeInput) {
                try {
                    const { validateReferralCode } = await import('./services/referralService');
                    const validReferrerId = await validateReferralCode(referralCodeInput);
                    if (validReferrerId) {
                        referrerId = validReferrerId;
                        console.log('[AUTH] ✅ Valid referral code, referrerId:', referrerId);
            } else {
                        console.warn('[AUTH] ⚠️ Invalid referral code, ignoring');
                    }
                } catch (referralError) {
                    console.error('[AUTH] ⚠️ Error validating referral code (non-blocking):', referralError);
                    // Don't block profile creation if referral validation fails
            }
            }

            // Generate referral code (fail-safe)
            try {
                const { generateReferralCode } = await import('./services/referralService');
                referralCode = await generateReferralCode(firebaseUser.uid, displayName);
                console.log('[AUTH] ✅ Generated referral code');
            } catch (referralGenError) {
                console.error('[AUTH] ⚠️ Error generating referral code (non-blocking):', referralGenError);
                // Don't block profile creation if referral code generation fails
            }

            // CRITICAL: Use ensureUserProfile - single source of truth
            const { ensureUserProfile } = await import('./services/profileService');
            const user = await ensureUserProfile(
                firebaseUser.uid,
                {
                    email: currentEmail,
                    displayName: firebaseUser.displayName || displayName,
                    photoURL: firebaseUser.photoURL,
                    provider: provider
                },
                {
                    username: userUsername ? userUsername.trim().toLowerCase() : undefined,
                    referralCode: referralCode,
                    referredBy: referrerId,
                    isAdmin: isAdmin
                }
            );

            console.log('[AUTH] ✅ Profile created/updated successfully');

            // Create referral document if user was referred (non-blocking)
            if (referrerId) {
                try {
                    const { createReferralDocument } = await import('./services/referralService');
                    await createReferralDocument(referrerId, firebaseUser.uid);
                    console.log('[AUTH] ✅ Referral document created');
                } catch (referralError) {
                    console.error('[AUTH] ⚠️ Error creating referral document (non-blocking):', referralError);
                    // Don't block login if referral creation fails
                }
            }

            onLoginSuccess(user);
        } catch (error: unknown) {
            const firestoreError = error as { code?: string; message?: string };
            
            // CRITICAL: Distinguish between Auth errors and Firestore errors
            console.error('[AUTH] ❌ CRITICAL ERROR DURING PROFILE CREATION:', {
                errorType: 'FIRESTORE_ERROR', // Explicitly mark as Firestore error
                code: firestoreError.code,
                message: firestoreError.message,
                uid: firebaseUser.uid,
                email: currentEmail,
                authSucceeded: true, // Auth already succeeded at this point
                firestoreFailed: true,
                errorObject: firestoreError,
                timestamp: new Date().toISOString(),
                environment: typeof window !== 'undefined' ? window.location.hostname : 'server'
            });
            
            // Map Firestore permission errors to user-friendly messages
            if (firestoreError.code === 'permission-denied') {
                throw new Error(t('auth.auth_error_generic'));
            } else {
                throw new Error(firestoreError.message || t('auth.auth_error_generic'));
            }
        }
    };

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Prevent double-submit
        if (loading) return;
        
        setError('');
        setUsernameError('');
        setLoading(true);
        try {
            if (isLogin) {
                // CRITICAL: Wait for auth to complete
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                
                // CRITICAL: Ensure auth is complete - userCredential.user.uid must be valid
                if (!userCredential.user || !userCredential.user.uid) {
                    throw new Error('Login failed: invalid user credential');
                }
                
                // CRITICAL: Create/update Firestore profile ONLY after auth is complete
                await checkOrCreateUserDoc(userCredential.user, undefined, undefined, 'password');
            } else {
                // SIGNUP FLOW - Create new user account
                // Validate username if provided (before auth to avoid unnecessary auth calls)
                if (username.trim()) {
                    const cleanUsername = username.trim().toLowerCase();
                    if (cleanUsername.length < 3) {
                        setUsernameError(t('settings.username_min_length'));
                        setLoading(false);
                        return;
                    }
                    if (!/^[a-z0-9_]+$/.test(cleanUsername)) {
                        setUsernameError(t('settings.username_invalid_chars'));
                        setLoading(false);
                        return;
                    }
                    // CRITICAL: Check username availability BEFORE creating auth account
                    const isAvailable = await checkUsernameAvailability(cleanUsername);
                    if (!isAvailable) {
                        setUsernameError(t('settings.username_taken'));
                        setLoading(false);
                        return;
                    }
                }
                
                // CRITICAL: Step 1 - Create Firebase Auth account
                console.log('[AUTH] 🔐 Step 1: Creating Firebase Auth account...');
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                
                // CRITICAL: Ensure auth is complete - userCredential.user.uid must be valid
                if (!userCredential.user || !userCredential.user.uid) {
                    throw new Error('Signup failed: invalid user credential');
                }
                
                console.log('[AUTH] ✅ Step 1: Firebase Auth account created, UID:', userCredential.user.uid);
                
                // CRITICAL: Step 2 - Create Firestore profile IMMEDIATELY after auth
                // This MUST happen synchronously and MUST succeed for the user to be discoverable
                // Path will be /users/{uid} where uid = userCredential.user.uid
                console.log('[AUTH] 🔐 Step 2: Creating Firestore profile document...');
                await checkOrCreateUserDoc(userCredential.user, name, username.trim(), 'password');
                console.log('[AUTH] ✅ Step 2: Firestore profile document created successfully');
            }
        } catch (err: unknown) {
            const error = err as { code?: string; message?: string };
            
            // CRITICAL: Log error with explicit type (Auth vs Firestore)
            const isAuthError = error.code?.startsWith('auth/');
            const isFirestoreError = error.message?.includes('permission') || error.message?.includes('Permission') || error.code === 'permission-denied';
            
            console.error('[AUTH] ❌ ERROR DURING EMAIL/PASSWORD AUTH:', {
                errorType: isAuthError ? 'AUTH_ERROR' : isFirestoreError ? 'FIRESTORE_ERROR' : 'UNKNOWN_ERROR',
                code: error.code,
                message: error.message,
                isAuthError,
                isFirestoreError,
                isLogin,
                timestamp: new Date().toISOString(),
                environment: typeof window !== 'undefined' ? window.location.hostname : 'server',
                errorObject: error
            });
            
            // Map Firebase errors to user-friendly messages
            if (error.code === 'auth/email-already-in-use') {
                setError(t('auth.auth_error_generic'));
            } else if (error.code === 'auth/wrong-password') {
                setError(t('auth.auth_loginErrorGeneric'));
            } else if (error.code === 'auth/user-not-found') {
                setError(t('auth.auth_loginErrorGeneric'));
            } else if (error.code === 'auth/invalid-email') {
                setError(t('auth.auth_error_generic'));
            } else if (error.code === 'auth/weak-password') {
                setError(t('auth.auth_error_generic'));
            } else if (error.code === 'auth/unauthorized-domain') {
                // CRITICAL: This error means the domain is not authorized in Firebase Console
                console.error('[AUTH] ❌ UNAUTHORIZED DOMAIN ERROR (Email/Password):', {
                    currentDomain: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
                    authDomain: 'vitacore-app-335cd.firebaseapp.com',
                    fix: 'Add domain to Firebase Console > Authentication > Settings > Authorized domains'
                });
                setError(t('auth.auth_error_generic'));
            } else if (isFirestoreError) {
                // Firestore permission error - auth succeeded but Firestore write failed
                setError(t('auth.auth_error_generic'));
            } else {
                setError(error.message || t('auth.auth_error_generic'));
            }
        } finally {
            setLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        setError('');
        setLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            // CRITICAL: Step 1 - Complete Google authentication
            console.log('[AUTH] 🔐 Step 1: Starting Google authentication...');
            const result = await signInWithPopup(auth, provider);
            
            // CRITICAL: Ensure auth is complete - result.user.uid must be valid
            if (!result.user || !result.user.uid) {
                throw new Error('Google sign-in failed: invalid user');
            }
            
            console.log('[AUTH] ✅ Step 1: Google authentication successful, UID:', result.user.uid);
            
            // CRITICAL: Step 2 - Create/update Firestore profile IMMEDIATELY after auth
            // This MUST happen synchronously and MUST succeed for the user to be discoverable
            console.log('[AUTH] 🔐 Step 2: Creating/updating Firestore profile document...');
            await checkOrCreateUserDoc(result.user, undefined, undefined, 'google');
            console.log('[AUTH] ✅ Step 2: Firestore profile document created/updated successfully');
        } catch (err: unknown) {
            const error = err as { code?: string; message?: string };
            // Handle user-friendly error messages with translations
            if (error.code === 'auth/popup-closed-by-user') {
                setError(t('auth.auth_cancelled'));
            } else if (error.code === 'auth/popup-blocked') {
                setError(t('auth.auth_popup_blocked'));
            } else if (error.code === 'auth/account-exists-with-different-credential') {
                // Account linking error - show translated message
                setError(t('auth.auth_accountExistsDifferentProvider'));
                // TODO: Implement advanced account linking flow in future
                // This would allow users to link Google/Facebook accounts to existing email/password accounts
            } else if (error.code === 'auth/unauthorized-domain') {
                // CRITICAL: This error means the domain is not authorized in Firebase Console
                console.error('[AUTH] ❌ UNAUTHORIZED DOMAIN ERROR (Google):', {
                    currentDomain: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
                    authDomain: 'vitacore-app-335cd.firebaseapp.com',
                    fix: 'Add domain to Firebase Console > Authentication > Settings > Authorized domains'
                });
                setError(t('auth.auth_error_generic'));
            } else {
                // Check for Firestore permission errors
                const isFirestoreError = error.message?.includes('permission') || error.message?.includes('Permission') || error.code === 'permission-denied';
                if (isFirestoreError) {
                    setError(t('auth.auth_error_generic'));
                } else {
                    setError(t('auth.auth_loginErrorGeneric') + ': ' + (error.message || ''));
                }
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center p-6 text-white relative tech-grid">
            <LanguageSelector lang={lang} setLang={setLang} />

            <div className="max-w-md w-full bg-[#0a0a0a] border border-slate-800/50 rounded-[3rem] p-10 z-10 shadow-[0_0_50px_rgba(0,255,255,0.05)] relative overflow-hidden group">
                {/* Decorative Tech Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 blur-3xl rounded-full" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/5 blur-3xl rounded-full" />

                <div className="text-center mb-10 relative">
                    <img src={LOGO_URL} alt="Logo" className="w-40 h-40 mx-auto mb-0 object-contain transition-all duration-500 group-hover:scale-110 drop-shadow-[0_0_15px_rgba(0,255,255,0.3)]" />
                    <h1 className="text-4xl font-light tracking-[0.15em] bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400 drop-shadow-lg leading-tight">
                        VitaCore
                    </h1>
                    <p className="text-slate-500 mt-3 text-[10px] font-black uppercase tracking-[0.2em]">{isLogin ? t('auth.login_subtitle') : t('onboarding.sub_4')}</p>
                </div>

                {error && <div className="bg-red-500/10 text-red-400 p-4 rounded-2xl mb-6 border border-red-500/20 text-xs font-bold flex items-center gap-2">
                    <AlertCircle size={14}/> {error}
                </div>}

                <form onSubmit={handleEmailAuth} className="space-y-5 relative">
                    {!isLogin && (
                        <>
                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 pl-1">{t('labels.name')}</label>
                            <div className="relative group/input">
                                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within/input:text-cyan-400 transition-colors" size={18} />
                                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#050505] border border-slate-800/80 p-4 pl-12 rounded-2xl outline-none focus:border-cyan-500/50 transition-all font-bold text-sm" required={!isLogin} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 pl-1">{t('auth.auth_username_optional')}</label>
                            <div className="relative group/input">
                                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within/input:text-cyan-400 transition-colors" size={18} />
                                <input 
                                    type="text" 
                                    value={username} 
                                    onChange={e => {
                                        const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                                        setUsername(val);
                                        setUsernameError('');
                                    }}
                                    className={`w-full bg-[#050505] border ${usernameError ? 'border-red-500' : 'border-slate-800/80'} p-4 pl-12 rounded-2xl outline-none focus:border-cyan-500/50 transition-all font-bold text-sm`}
                                    placeholder="username_123"
                                />
                            </div>
                            {usernameError && (
                                <p className="text-red-400 text-xs mt-1">{usernameError}</p>
                            )}
                            {username && !usernameError && (
                                <p className="text-emerald-400 text-xs mt-1">{t('auth.username_available')}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 pl-1">{t('auth.login_ref')}</label>
                            <div className="relative group/input">
                                <Ticket className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within/input:text-cyan-400 transition-colors" size={18} />
                                <input type="text" value={referralCodeInput} onChange={e => setReferralCodeInput(e.target.value.toUpperCase())} className="w-full bg-[#050505] border border-slate-800/80 p-4 pl-12 rounded-2xl outline-none focus:border-cyan-500/50 transition-all font-bold text-sm" placeholder="VITACORE-XXXX" />
                            </div>
                        </div>
                        </>
                    )}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 pl-1">{t('auth.login_email')}</label>
                        <div className="relative group/input">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within/input:text-cyan-400 transition-colors" size={18} />
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-[#050505] border border-slate-800/80 p-4 pl-12 rounded-2xl outline-none focus:border-cyan-500/50 transition-all font-bold text-sm" required />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 pl-1">{t('auth.login_password')}</label>
                        <div className="relative group/input">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within/input:text-cyan-400 transition-colors" size={18} />
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-[#050505] border border-slate-800/80 p-4 pl-12 rounded-2xl outline-none focus:border-cyan-500/50 transition-all font-bold text-sm" required />
                        </div>
                    </div>

                    <button type="submit" disabled={loading} className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-black py-5 rounded-[2rem] flex items-center justify-center gap-2 transition-all shadow-[0_10px_30px_rgba(0,255,255,0.2)] active:scale-[0.98]">
                        {loading ? <RefreshCw className="animate-spin" /> : (isLogin ? t('auth.login_btn') : t('auth.login_signup_btn'))}
                        {!loading && <ChevronRight size={18} />}
                    </button>
                </form>

                {/* Social Login Buttons */}
                <div className="mt-6 space-y-3">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-slate-800"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="px-4 bg-[#0a0a0a] text-slate-500 font-black tracking-widest">{t('auth.auth_or')}</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={signInWithGoogle}
                        disabled={loading}
                        className="w-full bg-white hover:bg-gray-100 text-gray-900 font-black py-4 rounded-[2rem] flex items-center justify-center gap-3 transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        {t('auth.login_google')}
                    </button>

                </div>

                <button onClick={() => setIsLogin(!isLogin)} className="w-full text-center mt-8 text-cyan-400 text-[10px] font-black uppercase tracking-[0.2em] hover:text-white transition-colors">
                    {isLogin ? t('auth.need_account') : t('auth.have_account')}
                </button>
            </div>
        </div>
    );
};
